import { lowerCase, capitalize } from "lodash";
import { notification } from "antd";
import { IconType } from "antd/es/notification/interface";
import moment from "moment";
import { RuleObject } from "antd/es/form";
// import { IconType } from "antd/lib/notification";

const Currency = require("currency.js");

class Util {
  static validateFreeEntryAmount = (rule: any, value: any, callback: any) => {
    const { field } = rule;

    if (!value || value.trim() === "") {
      return callback("Please specify amount");
    } else if (Number(value) < 10) {
      callback("Amount cannot be less than ₦10");
      return;
    }
    callback();
  };
  static validateTextFieldWithCustomValues = (
    rule: any,
    value: any,
    callback: any,
    customValues: string[]
  ) => {
    if (
      value &&
      value.trim() !== "" &&
      !customValues.map((item) => item.toLowerCase().trim()).includes(value.toLowerCase().trim())
    ) {
      callback("You have entered an incorrect value, please choose from the list");
      return;
    }
    callback();
  };

  static disablePastDates = (currentDate: any) => {
    return moment().add(-1, "days") >= currentDate || moment().add(1, "month") <= currentDate;
  };

  static validateDateRange = (value: any, startDate?: string, endDate?: string): string | null => {
    if (value == null || !value) return null;
    const raw = moment(value?.$d || value);
    if (!raw.isValid()) return "Invalid date";
    const valueUtc = moment.utc(raw.format("YYYY-MM-DD"), "YYYY-MM-DD");
    if (startDate) {
      const startUtc = moment.utc(startDate, "YYYY-MM-DD").startOf("day");
      if (valueUtc.isBefore(startUtc))
        return `Date must be on or after ${moment.utc(startDate, "YYYY-MM-DD").format("YYYY-MM-DD")}`;
    }
    if (endDate) {
      const endUtc = moment.utc(endDate, "YYYY-MM-DD").startOf("day");
      if (valueUtc.isAfter(endUtc))
        return `Date must be on or before ${moment.utc(endDate, "YYYY-MM-DD").format("YYYY-MM-DD")}`;
    }
    return null;
  };

  static dateRangeRule = (startDate?: string, endDate?: string) => ({
    validator: (_: any, value: any) => {
      const err = Util.validateDateRange(value, startDate, endDate);
      return err ? Promise.reject(err) : Promise.resolve();
    },
  });

  static isDevelopmentMode = () => {
    return process.env.NODE_ENV === "development";
  };

  static numberWithCommas = (x: number) => {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  static stripCommaFromAmount = (x: string) => {
    return x.replace(/,/g, "");
  };

  static formatAmount = (symbol: string, amount: number) => {
    if (amount === undefined || amount === null) {
      return "₦ -.--";
    }

    if (symbol === "NGN") {
      symbol = "₦";
    } else {
      symbol = symbol || "₦";
    }
    symbol = symbol + "";

    let currencyOption = {
      symbol: symbol,
    };

    let currency = Currency(amount, currencyOption);
    return currency.format(true);
  };

  static getGeneralStatusTagClass = (status: string) => {
    status = status && lowerCase(status);

    switch (status.trim()) {
      case "one time":
      case "single":
        return "one-time";
      case "subscription":
        return "subscription";
      case "success":
      case "paid":
      case "credit":
      case "active":
      case "fixed":
      case "overpaid": {
        return "success";
      }
      case "debit":
      case "failed": {
        return "failed";
      }
      case "underpaid":
      case "invoice":
      case "pending": {
        return "pending";
      }

      case "cancelled":
        return "cancelled";
      default: {
        return "unknown";
      }
    }
  };

  static normalizePaymentStatus = (paymentStatus: string) => {
    paymentStatus = paymentStatus && paymentStatus.replace("_", " ");
    return capitalize(paymentStatus);
  };

  static normalizePaymentMethod = (paymentMethod: string) => {
    paymentMethod = paymentMethod && paymentMethod.replace("_", " ");
    return capitalize(paymentMethod);
  };

  static formHasErrors(fieldsError: any) {
    return Object.keys(fieldsError).some((field) => fieldsError[field]);
  }

  static extractErrors = (error: any) => {
    if (typeof error.response.data.error === "string")
      return [error.response.data.message || error.response.data.error];
    if (error.response) {
      if (error.response.data.error && error.response.data.error.length > 0) {
        return error.response.data.error.map((error: any) => {
          return error.message;
        });
      } else if (error.response.data.message) {
        return [error.response.data.message];
      } else {
        return [error.response.data.error];
      }
    } else {
      return [];
    }
  };

  static showError = (response: any) => {
    let errors = Util.extractErrors(response);
    if (errors && errors.length > 0) {
      // You can put your code here to handle the error
      Util.showMessage("Request Failed", errors[0], "error");
    }
  };

  static showMessage = (title: any, description: any, type: IconType | undefined) => {
    notification.open({
      type: type || "success",
      message: title,
      description: description,
    });
  };

  static isValidEmail = (value: string) => {
    // eslint-disable-next-line no-useless-escape
    // let validator = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    let validator = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,7}$/g;

    return value.match(validator);
  };

  static validateNumberRange = (
    value: any,
    minValue?: number,
    maxValue?: number
  ): string | null => {
    if (value == null || value === "") return null;
    const n = Number(value);
    if (Number.isNaN(n)) return "Invalid number";
    if (minValue != null && n < minValue) return `Must be at least ${minValue}`;
    if (maxValue != null && n > maxValue) return `Must be at most ${maxValue}`;
    return null;
  };

  static numberRangeRule = (minValue?: number, maxValue?: number) => ({
    validator: (_: any, value: any) => {
      const err = Util.validateNumberRange(value, minValue, maxValue);
      return err ? Promise.reject(err) : Promise.resolve();
    },
  });

  static validateTextLength = (value: any, minLen?: number, maxLen?: number): string | null => {
    if (value == null || value === "") return null;
    const len = String(value).length;
    if (minLen != null && len < minLen) return `Must be at least ${minLen} characters`;
    if (maxLen != null && len > maxLen) return `Must be at most ${maxLen} characters`;
    return null;
  };

  static textLengthRule = (minLen?: number, maxLen?: number) => ({
    validator: (_: any, value: any) => {
      const err = Util.validateTextLength(value, minLen, maxLen);
      return err ? Promise.reject(err) : Promise.resolve();
    },
  });

  static parseValidationValue = (data: {
    validationValue?: string | string[];
  }): Record<string, number | string> => {
    const raw = data.validationValue;
    if (raw == null || typeof raw !== "string") return {};
    try {
      return JSON.parse(raw) as Record<string, number | string>;
    } catch {
      return {};
    }
  };

  static getSelectOptionList = (
    values: string | string[] | undefined
  ): { label: string; value: string }[] => {
    if (!values) return [];
    const list = typeof values === "string" ? JSON.parse(values) : values;
    return (list || []).map((item: string) => ({ label: item, value: item }));
  };

  static validateEmail = (rule: any, value: any, callback: any) => {
    if (!value || !Util.isValidEmail(value)) {
      return callback("Please specify a valid email.");
    }

    callback();
  };
}
export default Util;
